import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { catchError, map, of, startWith, switchMap } from 'rxjs';

import { ScreenFieldModel } from '@models/module/fields/main';
import { GridSearchEntityInstance } from '@models/module/grid';
import { DataCard } from '@models/module/main';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import { Currency, CurrencyFn } from '@models/system';
import {
  DATA_CARD_FRAME_CSS,
  DATA_CARD_SINGLE_COLUMN_MAX_WIDTH,
  DATA_CARD_SKELETON_FALLBACK_HEIGHT,
  getDataCardGridHeight,
  getDataCardVirtualRowHeight,
} from '@module-instance/data-card/data-card-layout';
import { DataCardViewComponent } from '@module-instance/data-card/data-card-view/data-card-view.component';
import { DataCardsViewService } from '@module-instance/data-card/data-cards-view/data-cards-view.service';
import { CipoTableOptions, TablePaginator } from '@shared/components/data-list/common';
import { CipoKeyboardListDirective, VirtualScrollTracker } from '@shared/components/virtual-list';
import { PAGE_SIZE_OPTIONS } from '@shared/consts';

import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { NotFoundComponent } from '@system/not-found/not-found.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

type CardFieldsState = {
  fields: ScreenFieldModel[];
  loading: boolean;
};

type DataCardVirtualRow = {
  cards: DataCard[];
  showSkeleton: boolean;
};

type CachedDataCard = {
  card: DataCard;
  currency: Currency | CurrencyFn;
  fields: ScreenFieldModel[];
};

const MAX_SKELETON_CARDS = 20;

@Component({
  selector: 'app-data-cards-view',
  templateUrl: './data-cards-view.component.html',
  styleUrls: ['./data-cards-view.component.scss'],
  imports: [
    DataCardViewComponent,
    MatPaginatorModule,
    NotFoundComponent,
    TranslateModule,
    NgxSkeletonLoaderModule,
    ScrollingModule,
    CipoKeyboardListDirective,
  ],
  // Publishes the frame metrics from data-card-layout so the SCSS paints the same box the virtual
  // row height is calculated from.
  host: {
    '[style.--data-card-frame-margin]': 'frameCss.margin',
    '[style.--data-card-frame-border]': 'frameCss.border',
    '[style.--data-card-frame-padding]': 'frameCss.padding',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataCardsViewComponent {
  private readonly cardsService = inject(DataCardsViewService);

  screenType = input.required<number>({ alias: 'screentype' });
  actions = input.required<DataListViewAction[]>();
  rows = input<GridSearchEntityInstance[]>([]);
  currency = input<Currency | CurrencyFn>();
  options = model<CipoTableOptions>({});
  isLoading = input.required<boolean>();
  infiniteScroll = input(false);

  cardClick = output<GridSearchEntityInstance>({ alias: 'cardclick' });
  /* TODO: Check this usage after cleanup Contracts from AngularJS */
  paginationUpdated = output<TablePaginator>();

  readonly frameCss = DATA_CARD_FRAME_CSS;

  private readonly viewport = viewChild(CdkVirtualScrollViewport);

  private readonly content = viewChild<ElementRef<HTMLElement>>('content');
  private lastRows: GridSearchEntityInstance[] = null;
  private pendingPageIndex: number = null;
  private lastLoadedPageIndex = 0;
  private readonly scrollTracker = new VirtualScrollTracker();
  private readonly loadedRows = signal<GridSearchEntityInstance[]>([]);
  private readonly cardCache = new WeakMap<GridSearchEntityInstance, CachedDataCard>();
  readonly columns = signal(2);

  private readonly fieldsState$ = toObservable(this.screenType).pipe(
    switchMap(type => {
      return this.cardsService.getScreenFields(type).pipe(
        map(response => ({ fields: response.data || [], loading: false }) as CardFieldsState),
        catchError(() => of({ fields: [], loading: false } as CardFieldsState)),
        startWith({ fields: [], loading: true } as CardFieldsState),
      );
    }),
  );

  private readonly fieldsState = toSignal(this.fieldsState$, {
    initialValue: { fields: [], loading: true } as CardFieldsState,
  });

  fields = computed(() => this.fieldsState().fields);

  cardGridHeight = computed(() => getDataCardGridHeight(this.fields()));
  virtualRowHeight = computed(() => getDataCardVirtualRowHeight(this.fields()));

  skeletonHeight = computed(() =>
    this.fields().length ? this.virtualRowHeight() : DATA_CARD_SKELETON_FALLBACK_HEIGHT,
  );

  cards = computed(() => {
    const rows = this.infiniteScroll() ? this.loadedRows() : this.rows();
    const fields = this.fields();
    const currency = this.currency();
    if (!rows?.length || !fields.length) return [];
    return rows.map(row => this.getDataCard(row, fields, currency));
  });

  virtualRows = computed<DataCardVirtualRow[]>(() => {
    const columns = this.columns();
    const cards = this.cards();
    const virtualRows: DataCardVirtualRow[] = [];

    for (let index = 0; index < cards.length; index += columns) {
      virtualRows.push({ cards: cards.slice(index, index + columns), showSkeleton: false });
    }

    if (this.isLoadingNextPage()) {
      const lastRow = virtualRows.at(-1);
      if (lastRow && lastRow.cards.length < columns) {
        lastRow.showSkeleton = true;
      } else {
        virtualRows.push({ cards: [], showSkeleton: true });
      }
    }

    return virtualRows;
  });

  serverPaginate = computed<TablePaginator | null>(() => {
    const opt = this.options();

    if (!opt?.serverPaginationSort || !opt?.pagination) return null;

    const { length, pageIndex, pageSize, pageSizeOptions } = opt.pagination;
    const size = pageSize ?? PAGE_SIZE_OPTIONS.default[1];
    const total = length ?? PAGE_SIZE_OPTIONS.default[1];

    return {
      length: total,
      pageIndex: pageIndex ?? 0,
      pageSize: size,
      pageSizeOptions: pageSizeOptions ?? PAGE_SIZE_OPTIONS.default,
      pagesCount: Math.ceil(total / size),
    };
  });

  loading = computed(() => this.isLoading() || this.fieldsState().loading);

  isInitialLoading = computed(() => this.loading() && !this.isLoadingNextPage());
  loadedRowsCount = computed(() => this.loadedRows().length);
  isLoadingNextPage = computed(() => this.infiniteScroll() && this.loading() && this.cards().length > 0);

  hasMoreRows = computed(() => {
    const total = this.options().pagination?.length ?? 0;
    return this.infiniteScroll() && total > this.loadedRows().length;
  });

  skeletonItems = computed(() => {
    const size = this.options()?.pagination?.pageSize || MAX_SKELETON_CARDS;
    return Array.from({ length: Math.min(size, MAX_SKELETON_CARDS) }, (_, i) => i);
  });

  constructor() {
    effect(() => {
      const rows = this.rows();
      const pageIndex = this.options().pagination?.pageIndex ?? 0;
      const useInfiniteScroll = this.infiniteScroll();

      if (!rows) {
        if (pageIndex === 0 || !useInfiniteScroll) {
          this.loadedRows.set([]);
          this.lastLoadedPageIndex = 0;
        }
        this.lastRows = null;
        this.pendingPageIndex = null;
        return;
      }

      if (rows === this.lastRows) {
        return;
      }
      this.lastRows = rows;

      const loadedPageIndex = this.pendingPageIndex ?? pageIndex;
      const appendRows = useInfiniteScroll && this.pendingPageIndex !== null && loadedPageIndex > 0;
      this.loadedRows.set(appendRows ? this.appendUniqueRows(rows) : rows);
      this.lastLoadedPageIndex = loadedPageIndex;
      this.pendingPageIndex = null;
    });

    effect(onCleanup => {
      const content = this.content();
      if (!content) {
        return;
      }

      const observer = new ResizeObserver(([entry]) => {
        const columns = entry.contentRect.width <= DATA_CARD_SINGLE_COLUMN_MAX_WIDTH ? 1 : 2;
        if (columns !== this.columns()) {
          this.columns.set(columns);
          // The row height is unchanged, but the number of rows is not — let the viewport remeasure.
          requestAnimationFrame(() => this.viewport()?.checkViewportSize());
        }
      });
      observer.observe(content.nativeElement);
      onCleanup(() => observer.disconnect());
    });
  }

  cardClickInternal(card: DataCard) {
    const rows = this.infiniteScroll() ? this.loadedRows() : this.rows();
    const row = rows.find(r => r.entity_instance_id === card.entity_instance_id);
    if (row) {
      this.cardClick.emit(row);
    }
  }

  paginationChange(event: PageEvent) {
    this.options.update(options => {
      if (!options?.pagination) {
        return options;
      }
      return {
        ...options,
        pagination: {
          ...options.pagination,
          pageIndex: event.pageIndex,
          pageSize: event.pageSize,
        },
      };
    });
    this.paginationUpdated.emit({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      length: event.length,
    });
  }

  onScrollIndexChange() {
    const viewport = this.viewport();
    if (!viewport) {
      return;
    }

    const { scrolledDown, canScroll, distanceToBottom } = this.scrollTracker.read(viewport);

    if ((scrolledDown || !canScroll) && distanceToBottom <= this.virtualRowHeight() * 2) {
      this.loadNextPage();
    }
  }

  trackByVirtualRow(index: number, row: DataCardVirtualRow): string {
    const keys = row.cards.map(card => card.entity_instance_id).join('-');
    return `${index}-${keys}-${row.showSkeleton}`;
  }

  private getDataCard(
    row: GridSearchEntityInstance,
    fields: ScreenFieldModel[],
    currency: Currency | CurrencyFn,
  ): DataCard {
    const cached = this.cardCache.get(row);
    if (cached?.fields === fields && cached.currency === currency) {
      return cached.card;
    }

    const card = this.cardsService.getDataCard(fields, row, currency);
    this.cardCache.set(row, { card, fields, currency });
    return card;
  }

  private appendUniqueRows(rows: GridSearchEntityInstance[]): GridSearchEntityInstance[] {
    const loaded = this.loadedRows();
    const known = new Set(loaded.map(row => row.entity_instance_id));
    return [...loaded, ...rows.filter(row => !known.has(row.entity_instance_id))];
  }

  private loadNextPage() {
    if (this.loading() || this.pendingPageIndex !== null || !this.hasMoreRows()) {
      return;
    }

    const pagination = this.options().pagination;
    const pageIndex = this.lastLoadedPageIndex + 1;
    this.pendingPageIndex = pageIndex;
    this.paginationUpdated.emit({
      pageIndex,
      length: pagination?.length ?? 0,
    });
  }
}
